<template>
  <div class="analytics-panel">
    <div class="analytics-toolbar">
      <h2>{{ $t('analytics.title') }}</h2>
      <div class="analytics-filters">
        <div class="filter-row">
          <div class="form-group">
            <label for="date-from">{{ $t('analytics.dateFrom') }}</label>
            <input id="date-from" v-model="dateFrom" type="date" class="form-control" />
          </div>
          <div class="form-group">
            <label for="date-to">{{ $t('analytics.dateTo') }}</label>
            <input id="date-to" v-model="dateTo" type="date" class="form-control" />
          </div>
          <button type="button" class="btn btn-primary" :disabled="loading" @click="fetchAnalytics">
            {{ $t('analytics.applyFilter') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="analytics-loading">
      <AppPageLoader>{{ $t('analytics.loading') }}</AppPageLoader>
    </div>

    <template v-else-if="data">
      <div class="kpi-cards">
        <div class="kpi-card">
          <span class="kpi-label">{{ $t('analytics.totalRevenue') }}</span>
          <span class="kpi-value">{{ formatCurrency(data.total_revenue) }}</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">{{ $t('analytics.totalOrders') }}</span>
          <span class="kpi-value">{{ data.total_orders }}</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">{{ $t('analytics.averageOrder') }}</span>
          <span class="kpi-value">{{ formatCurrency(data.average_order) }}</span>
        </div>
      </div>

      <div class="analytics-grid">
        <div class="analytics-card">
          <h3>{{ $t('analytics.revenueByDay') }}</h3>
          <div v-if="data.revenue_by_day.length === 0" class="empty-message">{{ $t('analytics.noData') }}</div>
          <div v-else class="revenue-list">
            <div
              v-for="day in data.revenue_by_day"
              :key="day.date"
              class="revenue-row"
            >
              <span class="revenue-date">{{ formatDateShort(day.date) }}</span>
              <span class="revenue-value">{{ formatCurrency(day.revenue) }}</span>
              <span class="revenue-orders">({{ day.orders_count }} {{ $t('analytics.ordersShort') }})</span>
            </div>
          </div>
        </div>

        <div class="analytics-card">
          <h3>{{ $t('analytics.topProducts') }}</h3>
          <div v-if="data.top_products.length === 0" class="empty-message">{{ $t('analytics.noData') }}</div>
          <div v-else class="table-wrap">
            <table class="analytics-table">
              <thead>
                <tr>
                  <th>{{ $t('analytics.product') }}</th>
                  <th class="num">{{ $t('analytics.quantitySold') }}</th>
                  <th class="num">{{ $t('analytics.revenue') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in data.top_products" :key="p.menu_item_id">
                  <td>{{ p.menu_item_name }}</td>
                  <td class="num">{{ p.quantity_sold }}</td>
                  <td class="num">{{ formatCurrency(p.revenue) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="analytics-card full-width">
          <div class="status-header">
            <h3>{{ $t('analytics.ordersByStatus') }}</h3>
            <div class="status-grid">
              <button
                v-for="status in visibleStatuses"
                :key="status"
                type="button"
                class="status-badge"
                :class="{ active: selectedStatus === status, interactive: canOpenOrders }"
                @click="toggleStatus(status)"
              >
                <span class="status-name">{{ formatOrderStatus(status) }}</span>
                <span class="status-count">{{ statusCount(status) }}</span>
              </button>
            </div>
          </div>

          <div v-if="canOpenOrders" class="status-orders">
            <div v-if="ordersLoading" class="empty-message">{{ $t('analytics.loadingOrders') }}</div>
            <div v-else-if="ordersError" class="empty-message">{{ ordersError }}</div>
            <div v-else-if="filteredOrders.length === 0" class="empty-message">{{ $t('analytics.noOrdersForStatus') }}</div>
            <ul v-else class="order-list">
              <li v-for="order in visibleOrders" :key="order.uuid" class="order-row">
                <div class="order-main">
                  <span class="order-number">#{{ orderLabel(order) }}</span>
                  <span class="order-customer">{{ order.user_name || $t('analytics.guest') }}</span>
                  <span class="order-status">{{ formatOrderStatus(order.status) }}</span>
                </div>
                <div class="order-meta">
                  <span v-if="order.table_name">{{ order.table_name }}</span>
                  <span>{{ formatDateTime(order.created_at) }}</span>
                </div>
                <div v-if="itemSummary(order)" class="order-items">{{ itemSummary(order) }}</div>
                <span class="order-amount">{{ formatCurrency(order.total_amount) }}</span>
              </li>
            </ul>
            <button
              v-if="filteredOrders.length > previewCount"
              type="button"
              class="view-more"
              @click="showAllOrders = !showAllOrders"
            >
              {{ showAllOrders ? $t('analytics.viewLess') : $t('analytics.viewMore') }}
              <span v-if="!showAllOrders">({{ filteredOrders.length - previewCount }})</span>
            </button>
          </div>
        </div>
      </div>
</template>

    <div v-else-if="error" class="analytics-error">
      <p>{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { establishmentApi, locationApi, ordersApi } from '@/services/api';
import type { AnalyticsResponse } from '@/types/analytics';
import type { OrderListItem } from '@/types/orders';
import { getApiErrorMessage } from '@/utils/apiErrors';

const props = withDefaults(
  defineProps<{
    type: 'establishment' | 'location';
    entityId: string;
  }>(),
  {}
);

const { t, locale } = useI18n();
const data = ref<AnalyticsResponse | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
function formatInputDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function thisWeekRange(): { from: string; to: string } {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const weekday = start.getDay();
  const daysFromMonday = weekday === 0 ? 6 : weekday - 1;
  start.setDate(start.getDate() - daysFromMonday);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { from: formatInputDate(start), to: formatInputDate(end) };
}

const week = thisWeekRange();
const dateFrom = ref(week.from);
const dateTo = ref(week.to);
const previewCount = 3;
const orderStatuses = [
  'pending',
  'confirmed',
  'in_process',
  'ready',
  'completed',
  'rejected',
  'canceled'
];

const visibleStatuses = computed(() => {
  const known = new Set(orderStatuses);
  const extra = Object.keys(data.value?.orders_by_status ?? {}).filter((status) => !known.has(status));
  return [...orderStatuses, ...extra];
});
const selectedStatus = ref<string | null>(null);
const orders = ref<OrderListItem[]>([]);
const ordersLoading = ref(false);
const ordersError = ref<string | null>(null);
const showAllOrders = ref(false);

const canOpenOrders = computed(() => props.type === 'location');

const filteredOrders = computed(() =>
  orders.value.filter((order) => {
    if (!orderInSelectedRange(order.created_at)) return false;
    if (selectedStatus.value && order.status !== selectedStatus.value) return false;
    return true;
  })
);

const visibleOrders = computed(() =>
  showAllOrders.value ? filteredOrders.value : filteredOrders.value.slice(0, previewCount)
);

function getParams() {
  const params: { date_from?: string; date_to?: string } = {};
  if (dateFrom.value) params.date_from = dateFrom.value;
  if (dateTo.value) params.date_to = dateTo.value;
  return Object.keys(params).length ? params : undefined;
}

async function fetchAnalytics() {
  if (!props.entityId) return;
  loading.value = true;
  error.value = null;
  try {
    if (props.type === 'establishment') {
      data.value = await establishmentApi.getAnalytics(props.entityId, getParams());
    } else {
      data.value = await locationApi.getAnalytics(props.entityId, getParams());
    }
  } catch (e: unknown) {
    error.value = getApiErrorMessage(e, t('general.error'));
    data.value = null;
  } finally {
    loading.value = false;
  }
  if (props.type === 'location' && data.value) {
    await loadOrders();
  }
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat(locale.value, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

function formatDateShort(dateStr: string): string {
  return new Intl.DateTimeFormat(locale.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(new Date(dateStr + 'T12:00:00'));
}

function statusCount(status: string): number {
  return data.value?.orders_by_status?.[status] ?? 0;
}

function formatOrderStatus(status: string): string {
  const key = `analytics.status_${status}`;
  const translated = t(key);
  return translated !== key ? translated : status;
}

function localDayStart(value: string): number {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
}

function localDayEnd(value: string): number {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
}

function orderInSelectedRange(createdAt: string): boolean {
  const time = new Date(createdAt).getTime();
  if (Number.isNaN(time)) return true;
  if (dateFrom.value) {
    const from = localDayStart(dateFrom.value);
    if (!Number.isNaN(from) && time < from) return false;
  }
  if (dateTo.value) {
    const to = localDayEnd(dateTo.value);
    if (!Number.isNaN(to) && time > to) return false;
  }
  return true;
}

function orderLabel(order: OrderListItem): string {
  if (order.display_order_number > 0) return String(order.display_order_number);
  return order.uuid.slice(0, 8);
}

function itemSummary(order: OrderListItem): string {
  const items = order.items ?? [];
  if (!items.length) return '';
  const parts = items.slice(0, 3).map((item) => `${item.quantity}× ${item.menu_item_name}`);
  const extra = items.length - 3;
  return extra > 0 ? `${parts.join(', ')} +${extra}` : parts.join(', ');
}

function formatDateTime(dateStr: string): string {
  return new Intl.DateTimeFormat(locale.value, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(dateStr));
}

async function loadOrders() {
  if (props.type !== 'location' || !props.entityId) return;
  ordersLoading.value = true;
  ordersError.value = null;
  try {
    orders.value = await ordersApi.list({
      location_id: props.entityId
    });
  } catch (e: unknown) {
    ordersError.value = getApiErrorMessage(e, t('general.error'));
    orders.value = [];
  } finally {
    ordersLoading.value = false;
  }
}

function toggleStatus(status: string) {
  if (!canOpenOrders.value) return;
  selectedStatus.value = selectedStatus.value === status ? null : status;
  showAllOrders.value = false;
}

watch(
  () => [props.entityId, props.type],
  () => {
    selectedStatus.value = null;
    orders.value = [];
    ordersError.value = null;
    showAllOrders.value = false;
    if (props.entityId) fetchAnalytics();
  },
  { immediate: false }
);

onMounted(() => {
  if (props.entityId) fetchAnalytics();
});
</script>

<style scoped>
.analytics-panel {
  padding: 0;
}

.analytics-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.5rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
}

.analytics-toolbar h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
}

.analytics-filters {
  margin-bottom: 0;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
}

.filter-row .form-group {
  margin-bottom: 0;
}

.filter-row .form-group label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.35rem;
  font-size: 0.9rem;
  color: #4b5563;
}

.form-control {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 0.95rem;
}

.form-control:focus {
  outline: none;
  border-color: #D8670C;
  box-shadow: 0 0 0 2px rgba(216, 103, 12, 0.15);
}

.analytics-loading,
.analytics-error {
  text-align: center;
  padding: 2rem 1.5rem;
  color: #6b7280;
}

.kpi-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
  margin-top: 1.5rem;
  margin-left: 1.5rem;
  margin-right: 1.5rem;
}

.kpi-card {
  background: #f9fafb;
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  border: 1px solid #f3f4f6;
}

.kpi-label {
  display: block;
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.kpi-value {
  font-size: 1.35rem;
  font-weight: 600;
  color: #111827;
}

.analytics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin: 0 1.5rem 1.5rem;
}

.analytics-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1.25rem;
}

.analytics-card.full-width {
  grid-column: 1 / -1;
}

.analytics-card h3 {
  margin: 0 0 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 0.75rem 1rem;
  flex-wrap: wrap;
}

.status-header h3 {
  margin: 0;
}

.empty-message {
  color: #6b7280;
  font-size: 0.95rem;
}

.revenue-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.revenue-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.35rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.revenue-date {
  min-width: 7rem;
  font-size: 0.9rem;
}

.revenue-value {
  font-weight: 600;
  color: #D8670C;
}

.revenue-orders {
  font-size: 0.85rem;
  color: #6b7280;
}

.table-wrap {
  overflow-x: auto;
}

.analytics-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

.analytics-table th,
.analytics-table td {
  padding: 0.5rem 0.75rem;
  text-align: left;
  border-bottom: 1px solid #f3f4f6;
}

.analytics-table th.num,
.analytics-table td.num {
  text-align: right;
}

.analytics-table th {
  font-weight: 600;
  color: #4b5563;
}

.status-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  border-radius: 0.5rem;
  border: 1px solid transparent;
  font: inherit;
  font-size: 0.9rem;
  color: inherit;
}

.status-badge.interactive {
  cursor: pointer;
}

.status-badge.interactive:hover,
.status-badge.active {
  background: #fff7ed;
  border-color: #D8670C;
}

.status-orders {
  margin-top: 1rem;
}

.order-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 22rem;
  overflow: auto;
}

.order-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.15rem 1rem;
  padding: 0.65rem 0.75rem;
  background: #f9fafb;
  border-radius: 0.5rem;
}

.order-main,
.order-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  min-width: 0;
}

.order-number {
  font-weight: 600;
  color: #111827;
}

.order-customer {
  color: #374151;
}

.order-status {
  font-size: 0.8rem;
  font-weight: 600;
  color: #D8670C;
  background: #fff7ed;
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
}

.order-meta,
.order-items {
  grid-column: 1 / -1;
  font-size: 0.85rem;
  color: #6b7280;
}

.order-amount {
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
}

.view-more {
  margin-top: 0.75rem;
  padding: 0.45rem 0.9rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: white;
  color: #374151;
  font-weight: 600;
  cursor: pointer;
}

.view-more:hover {
  border-color: #D8670C;
  color: #D8670C;
}

.status-name {
  color: #374151;
}

.status-count {
  font-weight: 600;
  color: #D8670C;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
}

.btn-primary {
  background: linear-gradient(135deg, #D8670C 0%, #c05a0a 100%);
  color: white;
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .analytics-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .analytics-grid {
    grid-template-columns: 1fr;
  }

  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
